/**
 * @packageDocumentation
 *
 * A [libp2p transport](https://docs.libp2p.io/concepts/transports/overview/) based on [WebSockets](https://developer.mozilla.org/en-US/docs/Web/API/WebSockets_API).
 *
 * @example
 *
 * ```js
 * import { createLibp2pNode } from 'libp2p'
 * import { webSockets } from '@libp2p/webrtc-direct'
 *
 * const node = await createLibp2p({
 *   transports: [
 *     webSockets()
 *   ]
 * //... other config
 * })
 * await node.start()
 * await node.dial('/ip4/127.0.0.1/tcp/9090/ws')
 * ```
 *
 * ## Filters
 *
 * When run in a browser by default this module will only connect to secure web socket addresses.
 *
 * To change this you should pass a filter to the factory function.
 *
 * You can create your own address filters for this transports, or rely in the filters [provided](./src/filters.js).
 *
 * The available filters are:
 *
 * - `filters.all`
 *   - Returns all TCP and DNS based addresses, both with `ws` or `wss`.
 * - `filters.dnsWss`
 *   - Returns all DNS based addresses with `wss`.
 * - `filters.dnsWsOrWss`
 *   - Returns all DNS based addresses, both with `ws` or `wss`.
 *
 * @example
 *
 * ```js
 * import { createLibp2pNode } from 'libp2p'
 * import { websockets } from '@libp2p/websockets'
 * import filters from '@libp2p/websockets/filters'
 * import { mplex } from '@libp2p/mplex'
 * import { noise } from '@libp2p/noise'
 *
 * const transportKey = Websockets.prototype[Symbol.toStringTag]
 * const node = await Libp2p.create({
 *   transport: [
 *     websockets({
 *       // connect to all sockets, even insecure ones
 *       filter: filters.all
 *     })
 *   ],
 *   streamMuxers: [
 *     mplex()
 *   ],
 *   connectionEncryption: [
 *     noise()
 *   ]
 * })
 * ```
 */
import { AbortError, CodeError } from '@libp2p/interface/errors';
import { symbol } from '@libp2p/interface/transport';
import { logger } from '@libp2p/logger';
import { multiaddrToUri as toUri } from '@multiformats/multiaddr-to-uri';
import { connect } from 'it-ws/client';
import pDefer from 'p-defer';
import { isBrowser, isWebWorker } from 'wherearewe';
import * as filters from './filters.js';
import { createListener } from './listener.js';
import { socketToMaConn } from './socket-to-conn.js';
const log = logger('libp2p:websockets');
class WebSockets {
    init;
    constructor(init) {
        this.init = init;
    }
    [Symbol.toStringTag] = '@libp2p/websockets';
    [symbol] = true;
    async dial(ma, options) {
        log('dialing %s', ma);
        options = options ?? {};
        const socket = await this._connect(ma, options);
        const maConn = socketToMaConn(socket, ma);
        log('new outbound connection %s', maConn.remoteAddr);
        const conn = await options.upgrader.upgradeOutbound(maConn);
        log('outbound connection %s upgraded', maConn.remoteAddr);
        return conn;
    }
    async _connect(ma, options) {
        if (options?.signal?.aborted === true) {
            throw new AbortError();
        }
        const cOpts = ma.toOptions();
        log('dialing %s:%s', cOpts.host, cOpts.port);
        const errorPromise = pDefer();
        const rawSocket = connect(toUri(ma), this.init);
        rawSocket.socket.addEventListener('error', () => {
            // the WebSocket.ErrorEvent type doesn't actually give us any useful
            // information about what happened
            // https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/error_event
            const err = new CodeError(`Could not connect to ${ma.toString()}`, 'ERR_CONNECTION_FAILED');
            log.error('connection error:', err);
            errorPromise.reject(err);
        });
        if (options.signal == null) {
            await Promise.race([rawSocket.connected(), errorPromise.promise]);
            log('connected %s', ma);
            return rawSocket;
        }
        // Allow abort via signal during connect
        let onAbort;
        const abort = new Promise((resolve, reject) => {
            onAbort = () => {
                reject(new AbortError());
                rawSocket.close().catch(err => {
                    log.error('error closing raw socket', err);
                });
            };
            // Already aborted?
            if (options?.signal?.aborted === true) {
                onAbort();
                return;
            }
            options?.signal?.addEventListener('abort', onAbort);
        });
        try {
            await Promise.race([abort, errorPromise.promise, rawSocket.connected()]);
        }
        finally {
            if (onAbort != null) {
                options?.signal?.removeEventListener('abort', onAbort);
            }
        }
        log('connected %s', ma);
        return rawSocket;
    }
    /**
     * Creates a Websockets listener. The provided `handler` function will be called
     * anytime a new incoming Connection has been successfully upgraded via
     * `upgrader.upgradeInbound`
     */
    createListener(options) {
        return createListener({ ...this.init, ...options });
    }
    /**
     * Takes a list of `Multiaddr`s and returns only valid Websockets addresses.
     * By default, in a browser environment only DNS+WSS multiaddr is accepted,
     * while in a Node.js environment DNS+{WS, WSS} multiaddrs are accepted.
     */
    filter(multiaddrs) {
        multiaddrs = Array.isArray(multiaddrs) ? multiaddrs : [multiaddrs];
        if (this.init?.filter != null) {
            return this.init?.filter(multiaddrs);
        }
        // Browser
        if (isBrowser || isWebWorker) {
            return filters.wss(multiaddrs);
        }
        return filters.all(multiaddrs);
    }
}
export function webSockets(init = {}) {
    return () => {
        return new WebSockets(init);
    };
}
//# sourceMappingURL=index.js.map